import type {TreeOption} from 'naive-ui';
import type {
    AssemblyComponentFixationState,
    AssemblyMateProjectionParticipant,
    DefaultDatumPlaneKind,
    EntityHighlightTarget,
    FeatureRollbackProjectionState,
    GlbDatumSlotTargetDescriptor,
    GlbNodeRole,
    SketchEntityReferenceWarningSummary,
} from '@puppycad/cad-core';

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/hooks/feature-tree` 目录；
 * 2. 负责集中定义特征树模块的共享类型、常量与元数据约定；
 * 3. 该文件只承载“设计树领域投影”的静态契约，不包含任何运行时业务逻辑。
 *
 * 文件职责：
 * 1. 为 `useFeatureTree.ts`、树投影构建器与动作处理器提供统一类型；
 * 2. 避免设计树元数据与节点分类散落在多个文件中重复定义；
 * 3. 为后续新增特征树分组、节点类型或动作能力提供单一扩展入口。
 */

/**
 * 特征树节点类别。
 */
export enum FeatureTreeNodeKind {
    /** 分组节点。 */
    Group = 'group',
    /** 基准面节点。 */
    DatumPlane = 'datum-plane',
    /** 基准轴节点。 */
    DatumAxis = 'datum-axis',
    /** 基准点节点。 */
    DatumPoint = 'datum-point',
    /** 对象节点。 */
    Object = 'object',
    /** GLB 导入模型节点。 */
    GlbNode = 'glb-node',
    /** GLB datum-slot 节点。 */
    GlbDatumSlot = 'glb-datum-slot',
    /** FeatureManager 退回控制棒节点。 */
    RollbackBar = 'rollback-bar',
    /** 装配体配合文件夹节点。 */
    AssemblyMateFolder = 'assembly-mate-folder',
    /** 装配体配合关系节点。 */
    AssemblyMate = 'assembly-mate',
}

/**
 * 特征树节点数据来源。
 */
export enum FeatureTreeDataSource {
    /** 来源于 Worker 状态。 */
    State = 'state',
    /** 来源于场景对象。 */
    Scene = 'scene',
    /** 来源于导入的 GLB 模型。 */
    Glb = 'glb',
}

/**
 * 特征树节点元数据。
 */
export interface FeatureTreeNodeMeta {
    /** 节点分类。 */
    kind: FeatureTreeNodeKind;
    /** 节点数据来源。 */
    source?: FeatureTreeDataSource;
    /** 关联对象 ID。 */
    objectId?: string;
    /** 关联对象类型。 */
    objectType?: string;
    /** 跨特征版本稳定的统一模型体逻辑 ID。 */
    modelBodyId?: string;
    /** 当前节点对应的统一 Worker 几何修订 ID。 */
    modelBodyRevisionId?: string;
    /** 创建该对象的特征 ID。 */
    createdByFeatureId?: string;
    /** 特征节点当前对应的结果体对象 ID 列表。 */
    resultObjectIds?: string[];
    /** 基准面 ID。 */
    planeId?: number;
    /** 基准轴 ID。 */
    axisId?: number;
    /** 基准点 ID。 */
    pointId?: number;
    /** 节点角色。 */
    nodeRole?: string;
    /** 特征种类。 */
    featureKind?: string;
    /** 源草图 ID。 */
    sourceSketchId?: string;
    /** 是否支持编辑特征。 */
    canEditFeature?: boolean;
    //------ 基准面编辑特征 开始------
    /** 是否支持基准面编辑特征。 */
    canEditReferencePlane?: boolean;
    //------ 基准面编辑特征 结束------
    //------ 基准轴编辑特征 开始------
    /** 是否支持基准轴编辑特征。 */
    canEditReferenceAxis?: boolean;
    //------ 基准轴编辑特征 结束------
    //------ 基准点编辑特征 开始------
    /** 是否支持基准点编辑特征。 */
    canEditReferencePoint?: boolean;
    //------ 基准点编辑特征 结束------
    /** 是否支持编辑草图。 */
    canEditSketch?: boolean;
    /** 当前特征在完整特征历史中的索引。 */
    featureCommandIndex?: number;
    /** 当前节点在完整 FeatureManager 退回序列中的索引。 */
    featureManagerIndex?: number;
    /** 是否位于退回控制棒下方。 */
    rolledBack?: boolean;
    /** 退回控制棒插入索引。 */
    rollbackCursorIndex?: number;
    /** 完整 FeatureManager 退回序列长度（仅控制棒元数据携带）。 */
    totalTimelineItemCount?: number;

    // ---- GLB 导入模型节点专用字段 ----
    /** GLB 节点语义角色（装配/零件/实体/实体文件夹等）。 */
    glbNodeRole?: GlbNodeRole;
    /**
     * 导入模型实体节点对应的稳定 bodyBindingId。
     *
     * `.puppy` 特征实体导入后，Body 节点是设计树语义节点，不一定拥有独立 Object3D。
     * 视口点/线/面代理需要通过该字段回流到正确实体节点。
     */
    bodyBindingId?: string;
    /** GLB 所属标签页 ID。 */
    glbTabId?: string;
    /** GLB 层级路径。 */
    glbPath?: string;
    /** 项目模型 nodeKey；仅装配项目容器节点具备，用于稳定定位结构节点。*/
    glbAssemblyNodeId?: string;
    /** GLB 原始 objectId（未经 selection 代理）。 */
    glbSourceObjectId?: string;
    /** 装配实例节点 ID，用于从视口点/线/面代理稳定回到组件树节点。 */
    assemblyNodeId?: string;
    /** 不含固定状态、配置与显示状态的装配节点权威名称。 */
    assemblyNodeDisplayName?: string;
    /** imported-model 层级解析出的 SOLIDWORKS 组件实例序号。 */
    assemblyNodeInstanceIndex?: number;
    /** 节点显示名称。 */
    displayName?: string;
    /** 引用外部文件 UUID。 */
    glbExternalFileUuid?: string;
    /** 引用外部文件 ID。 */
    glbExternalFileId?: string;
    /** 引用外部装配体名称。 */
    glbExternalAssemblyName?: string;
    /** 装配体零部件固定/浮动状态。 */
    assemblyFixationState?: AssemblyComponentFixationState;
    /** 是否支持设计树右键固定/浮动。 */
    canFixOrFloat?: boolean;
    /** 是否可被选中。 */
    selectable?: boolean;
    /** 当前设计树投影中的隐藏状态；主要供非设计树组件复用同一套菜单文案。 */
    statusHidden?: boolean;
    /** 实体节点对应的 glTF primitives 高亮目标。 */
    highlightTargets?: EntityHighlightTarget[];
    /** 导入 GLB 实体对应的 bodyBindingId，用于 baseline 消耗后隐藏设计树节点。 */
    glbBodyBindingId?: string;
    /** GLB datum-slot 描述符。 */
    glbDatumSlot?: GlbDatumSlotTargetDescriptor;
    /** GLB datum-slot 槽位类型。 */
    glbDatumSlotKind?: GlbDatumSlotTargetDescriptor['slotKind'];
    /** 装配体 Mate ID。 */
    mateId?: string;
    /** 装配体 Mate 类型。 */
    mateType?: string;
    /** 装配体 Mate 是否被抑制。 */
    mateSuppressed?: boolean;
    /** 装配体 Mate 求解状态。 */
    mateSolveStatus?: string;
    /** 装配体 Mate 错误信息。 */
    mateErrorMessage?: string;
    /** Mate 两端参与实体摘要。 */
    mateParticipants?: readonly AssemblyMateProjectionParticipant[];
    /** 配合文件夹所属装配体节点 ID。 */
    assemblyMateOwnerAssemblyNodeId?: string;
    /** Mate in assembly 文件夹所属组件或子装配体节点 ID。 */
    assemblyMateOwnerComponentNodeId?: string;
}

/**
 * 判断设计树节点是否携带有效基准面 ID。
 *
 * 说明：
 * 1. 前视基准面是首个创建的 ReferencePlane，其 planeId 为 0；
 * 2. 不能用 truthy 判断，否则前视基准面的 hover / 选中 / 草图联动会全部失效。
 *
 * @param planeId 节点元数据中的基准面 ID。
 * @returns 是否为有效基准面 ID。
 */
export function hasFeatureTreeDatumPlaneId(planeId: number | undefined): planeId is number {
    return typeof planeId === 'number';
}

/**
 * 设计树外部依赖提供者。
 */
/**
 * 设计树草图生命周期动作。
 */
export type FeatureTreeSketchLifecycleAction =
    | 'delete'
    | 'hide'
    | 'show'
    | 'copy';

//------ 基准对象删除历史 开始------
/**
 * 设计树基准对象类型。
 */
export enum FeatureTreeDatumReferenceKind {
    /** 基准面。 */
    Plane = 'datum-plane',
    /** 基准轴。 */
    Axis = 'datum-axis',
    /** 基准点。 */
    Point = 'datum-point',
}

/**
 * 设计树基准对象删除动作输入。
 */
export interface FeatureTreeDatumReferenceDeleteActionInput {
    /** 基准对象类型。 */
    readonly kind: FeatureTreeDatumReferenceKind;
    /** 基准对象稳定 ID。 */
    readonly datumId: number;
    /** 触发动作的设计树节点 key。 */
    readonly treeKey: string;
}
//------ 基准对象删除历史 结束------

/**
 * 设计树草图生命周期动作输入。
 */
export interface FeatureTreeSketchLifecycleActionInput {
    /** 动作类型。 */
    readonly action: FeatureTreeSketchLifecycleAction;
    /** 草图组 ID。 */
    readonly sketchGroupId: string;
    /** 触发动作的设计树节点 key。 */
    readonly treeKey: string;
}

/**
 * 设计树特征删除动作输入。
 */
export interface FeatureTreeFeatureDeleteActionInput {
    /** 待删除的特征 ID。 */
    readonly featureId: string;
    /** 触发动作的设计树节点 key。 */
    readonly treeKey: string;
    /** 设计树名称覆盖表，用于确认框展示用户重命名后的特征名称。 */
    readonly objectNameOverrides?: ReadonlyMap<string, string>;
}

/**
 * 设计树装配组件删除动作输入。
 */
export interface FeatureTreeAssemblyComponentDeleteActionInput {
    /** 触发动作的设计树节点 key。 */
    readonly treeKey: string;
    /** 项目模型 stable nodeKey，用于优先定位装配组件。 */
    readonly nodeKey?: string;
    /** 当前树节点用于选择或高亮的对象 ID。 */
    readonly objectId: string;
    /** GLB 层级中的原始对象 ID。 */
    readonly sourceObjectId?: string;
    /** 设计树显示名称。 */
    readonly displayName?: string;
    /** GLB 节点角色。 */
    readonly nodeRole: GlbNodeRole;
    /** 是否补充当前 Viewer 选中对象的父级链作为定位候选。 */
    readonly includeViewerSelectionAncestors?: boolean;
}

/**
 * 设计树特征显隐动作输入。
 */
export interface FeatureTreeFeatureVisibilityActionInput {
    /** 动作类型。 */
    readonly action: 'hide' | 'show';
    /** 特征 ID。 */
    readonly featureId: string;
    /** 触发动作的设计树节点 key。 */
    readonly treeKey: string;
}

/**
 * 退回控制棒动作输入。
 */
export type FeatureTreeRollbackActionInput =
    | {
        /** 移动到指定 FeatureManager cursor。 */
        readonly action: 'move-to-index';
        /** 目标 cursor。 */
        readonly cursorIndex: number;
    }
    | {
        /** 退回到指定特征之前。 */
        readonly action: 'rollback-to-feature';
        /** 目标特征 ID。 */
        readonly featureId: string;
        /** 触发操作的设计树 key。 */
        readonly treeKey: string;
    }
    | {
        /** 前滚到指定特征之后。 */
        readonly action: 'roll-forward';
        /** 目标特征 ID；缺省时前滚一个 FeatureManager 节点。 */
        readonly featureId?: string;
        /** 触发操作的设计树 key。 */
        readonly treeKey?: string;
    }
    | {
        /** 回到上一退回位置。 */
        readonly action: 'roll-to-previous';
    }
    | {
        /** 移动到模型末尾。 */
        readonly action: 'roll-to-end';
    };

export interface FeatureTreeProviders {
    /** NaiveUI message 实例。 */
    message: {
        /** 信息提示。 */
        info: (content: string) => void;
        /** 警告提示。 */
        warning: (content: string) => void;
    };
    /** NaiveUI dialog 实例。 */
    dialog: {
        /** 创建对话框。 */
        create: (options: Record<string, unknown>) => unknown;
    };
    /** 在基准面上进入草图编辑。 */
    enterSketchEditingOnDatumPlane: (planeId: number, planeName?: string) => void | Promise<void>;
    /** 执行草图命令。 */
    handleSketchCommand: (command: string) => Promise<void>;
    /** 编辑特征回调。 */
    handleFeatureEdit?: (input: {
        /** 特征 ID。 */
        featureId: string;
        /** 特征种类。 */
        featureKind: string;
        /** 源草图 ID。 */
        sourceSketchId?: string;
    }) => Promise<void> | void;
    //------ 基准面编辑特征 开始------
    /** 编辑基准面特征回调。 */
    handleReferencePlaneEdit?: (input: {
        /** 基准面 ID。 */
        planeId: number;
        /** 触发操作的设计树 key。 */
        treeKey: string;
    }) => Promise<void> | void;
    //------ 基准面编辑特征 结束------
        //------ 基准轴编辑特征 开始------
        /** 编辑基准轴特征回调。 */
        handleReferenceAxisEdit?: (input: {
            /** 基准轴 ID。 */
            axisId: number;
            /** 触发操作的设计树 key。 */
            treeKey: string;
        }) => Promise<void> | void;
        //------ 基准轴编辑特征 结束------
    //------ 基准点编辑特征 开始------
    /** 编辑基准点特征回调。 */
    handleReferencePointEdit?: (input: {
        /** 基准点 ID。 */
        pointId: number;
        /** 触发操作的设计树 key。 */
        treeKey: string;
    }) => Promise<void> | void;
    //------ 基准点编辑特征 结束------
    /**
     * 编辑草图回调。
     *
     * 返回 false 表示底层草图会话未恢复成功，动作层不应继续写入设计树选中态。
     */
    handleFeatureEditSketch?: (input: EditSketchRequest) => Promise<boolean | void> | boolean | void;
    /** 通过 Editor 历史边界执行设计树草图删除、隐藏、显示和复制。 */
    handleSketchLifecycleAction?: (input: FeatureTreeSketchLifecycleActionInput) => Promise<boolean> | boolean;
    //------ 基准对象删除历史 开始------
    /** 通过 Editor 历史边界执行设计树基准对象删除。 */
    handleDatumReferenceDeleteAction?: (input: FeatureTreeDatumReferenceDeleteActionInput) => Promise<boolean> | boolean;
    //------ 基准对象删除历史 结束------
    /** 通过 Editor 历史边界执行设计树特征删除。 */
    handleFeatureDeleteAction?: (input: FeatureTreeFeatureDeleteActionInput) => Promise<boolean> | boolean;
    /** 通过 Editor 装配结构边界执行设计树装配组件删除。 */
    handleAssemblyComponentDeleteAction?:
        (input: FeatureTreeAssemblyComponentDeleteActionInput) => Promise<boolean> | boolean;
    /** 通过 Editor 历史边界执行设计树特征隐藏或显示。 */
    handleFeatureVisibilityAction?: (input: FeatureTreeFeatureVisibilityActionInput) => Promise<boolean> | boolean;
    /** 通过 Editor 历史边界执行退回控制棒动作。 */
    handleFeatureRollbackAction?: (input: FeatureTreeRollbackActionInput) => Promise<boolean> | boolean;
    /** 切换装配体零部件固定/浮动状态。 */
    handleAssemblyComponentFixationChange?: (input: {
        /** hierarchy objectId 或 selectionObjectId。 */
        objectId: string;
        /** 场景对象 ID；缺省时由 provider 复用 objectId。 */
        sceneObjectId?: string;
        /** 目标固定/浮动状态。 */
        state: AssemblyComponentFixationState;
        /** 触发操作的设计树 key。 */
        treeKey: string;
    }) => Promise<void> | void;
    /** 从设计树进入已提交 Mate 编辑 PropertyManager。 */
    handleAssemblyMateEdit?: (input: {
        /** 要编辑的 Mate ID。 */
        mateId: string;
        /** 触发操作的设计树 key。 */
        treeKey: string;
    }) => Promise<boolean | void> | boolean | void;
    //------ 线性零部件阵列节点删除 开始------
    /** 删除线性零部件阵列分组节点。 */
    handleLinearComponentPatternDelete?: (input: {
        /** 线性阵列分组对象 ID。 */
        groupObjectId: string;
        /** 触发操作的设计树 key。 */
        treeKey: string;
    }) => Promise<void> | void;
    /** 从特征树编辑线性零部件阵列分组节点。 */
    handleLinearComponentPatternEdit?: (input: {
        /** 线性阵列分组对象 ID。 */
        groupObjectId: string;
        /** 触发操作的设计树 key。 */
        treeKey: string;
    }) => Promise<void> | void;
    //------ 线性零部件阵列节点删除 结束------
}

/**
 * 编辑草图入口来源。
 */
export type EditSketchRequestSource =
    /** 设计树草图节点入口。 */
    | 'sketch-node'
    /** 设计树特征节点入口。 */
    | 'feature-node';

/**
 * 编辑草图请求对象。
 *
 * 设计说明：
 * 1. 设计树动作层必须先把 feature 节点与 sketch 节点归一到 `sketchGroupId`；
 * 2. Editor 只消费“恢复指定草图编辑会话”这一种语义，避免两条入口在工具栏状态和重建链路上分叉；
 * 3. `source` 仅用于追踪触发来源和后续扩展，不允许 provider 再用来源分支改变草图编辑行为。
 */
export interface EditSketchRequest {
    /** 草图组 uuid。 */
    sketchGroupId: string;
    /** 触发编辑草图的来源。 */
    source: EditSketchRequestSource;
    /** 触发操作的设计树节点 key。 */
    triggerNodeKey: string;
    /** 来源特征 ID；仅 feature-node 入口存在。 */
    featureId?: string;
    /** 来源特征种类；仅 feature-node 入口存在。 */
    featureKind?: string;
}

/**
 * 草图生命周期快照。
 */
export interface SketchLifecycleEntry {
    /** 是否隐藏。 */
    hidden?: boolean;
    /** 消费当前草图的特征列表。 */
    consumedByFeatureIds?: string[];
    /** 依赖当前草图的特征列表。 */
    dependentFeatureIds?: string[];
    /** 主吸收特征 ID。 */
    primaryAbsorbedByFeatureId?: string;
    /** 草图角色。 */
    role?: string;
}

/**
 * 受管草图摘要。
 */
export interface ManagedSketchSummary {
    /** 草图对象运行时 ID（场景句柄）。 */
    id: string;
    /**
     * 草图跨会话稳定持久 ID。
     * 特征绑定原生记录该身份，设计树投影据此判定草图是否已被特征吸收。
     */
    persistentId?: string;
    /** 草图实体类型。 */
    entityType: string;
    /** 草图名称。 */
    name: string;
    /** 历史顺序。 */
    order: number;
    /** 草图创建时间戳（毫秒），用于设计树时间线排序。 */
    createdAt?: number;
    /**
     * 是否为当前正在编辑的草图。
     * 该状态只参与设计树交互投影，不属于文档持久化状态。
     */
    editing?: boolean;
    /** 转换实体引用悬空警告摘要。 */
    referenceWarning?: SketchEntityReferenceWarningSummary;
}

/**
 * 特征树状态对象摘要。
 */
export interface FeatureTreeObjectSummary {
    /** 对象 ID。 */
    id: string | number;
    /** 对象类型。 */
    type: string;
    /** 对象元数据。 */
    metadata?: Record<string, unknown>;
    /** 关联命令 ID。 */
    commandId?: string;
    /** 显示名称。 */
    name: string;
}

/**
 * 基准面树投影摘要。
 */
export interface FeatureTreeDatumPlaneSummary {
    /** 基准面 ID。 */
    id: number;
    /** 基准面名称。 */
    name: string;
    //------ 默认基准面稳定身份 开始------
    /** 默认内置基准面稳定身份；用户创建的基准面为空。 */
    defaultPlaneKind?: DefaultDatumPlaneKind | null;
    //------ 默认基准面稳定身份 结束------
    //------ 基准面编辑特征 开始------
    /** 是否支持基准面编辑特征。 */
    canEditReferencePlane?: boolean;
    //------ 基准面编辑特征 结束------
    /** 用户手动创建时间戳（毫秒），用于设计树时间线排序。 */
    createdAt?: number;
    /** 是否可见。 */
    isVisible: () => boolean;
}

/**
 * 基准轴树投影摘要。
 */
export interface FeatureTreeDatumAxisSummary {
    /** 基准轴 ID。 */
    id: number;
    /** 基准轴名称。 */
    name: string;
    //------ 基准轴编辑特征 开始------
    /** 是否支持基准轴编辑特征。 */
    canEditReferenceAxis?: boolean;
    //------ 基准轴编辑特征 结束------
    /** 用户手动创建时间戳（毫秒），用于设计树时间线排序。 */
    createdAt?: number;
    /** 是否可见。 */
    isVisible: () => boolean;
}

/**
 * 基准点树投影摘要。
 */
export interface FeatureTreeDatumPointSummary {
    /** 基准点 ID。 */
    id: number;
    /** 基准点名称。 */
    name: string;
    //------ 基准点编辑特征 开始------
    /** 是否支持基准点编辑特征。 */
    canEditReferencePoint?: boolean;
    //------ 基准点编辑特征 结束------
    /** 是否为默认原点。 */
    isOrigin: boolean;
    /** 用户手动创建时间戳（毫秒），用于设计树时间线排序。 */
    createdAt?: number;
    /** 是否可见。 */
    isVisible: () => boolean;
}

/**
 * 特征树投影输入。
 */
export interface BuildFeatureTreeProjectionOptions {
    /** Worker 对象列表。 */
    objects: any[];
    /** 命令历史。 */
    history: any[];
    /** 基准面列表。 */
    datumPlanes: FeatureTreeDatumPlaneSummary[];
    /** 基准轴列表。 */
    datumAxes: FeatureTreeDatumAxisSummary[];
    /** 基准点列表。 */
    datumPoints: FeatureTreeDatumPointSummary[];
    /** 场景草图摘要。 */
    sceneSketches: ManagedSketchSummary[];
    /** 隐藏对象集合。 */
    hiddenObjectIds: Set<string>;
    /** 压制草图集合。 */
    suppressedSketchIds: Set<string>;
    /** 对象名称覆写表。 */
    objectNameOverrides: Map<string, string>;
    /**
     * 零件根节点显示名称。
     * 用于把 part-root 与当前 tab 标签对齐；缺省时回落到旧的占位文案。
     */
    partLabel?: string;
    /** cad-core 退回控制棒投影状态。 */
    rollbackState?: FeatureRollbackProjectionState | null;
}

/**
 * 特征树投影输出。
 */
export interface FeatureTreeProjectionResult {
    /** 树节点数据。 */
    featureTreeData: TreeOption[];
    /** 节点元数据映射。 */
    featureTreeNodeMap: Map<string, FeatureTreeNodeMeta>;
}

/**
 * 默认基准面显示顺序。
 */
export const DEFAULT_PLANE_ORDER = ['前视基准面', '上视基准面', '右视基准面'] as const;
